import { getConfig } from '@edx/frontend-platform';
import { getAuthenticatedHttpClient } from '@edx/frontend-platform/auth';

/**
 * Regional (PPP) pricing for the logged-in learner.
 *
 * This dashboard reads prices from DDN, which bypasses Django entirely, so
 * unlike the anonymous storefront the server has no channel to inject the
 * discount into the price it serves. Instead we fetch the learner's region
 * once and apply the percentage client-side.
 *
 * That makes `applyRegionalPrice` below SAFETY-CRITICAL: checkout goes through
 * blendxapi, which applies the discount server-side, so if this function and
 * blendxapi's `regional_price` ever round differently the learner is quoted
 * one price and charged another. It is a deliberate line-by-line mirror of
 * blendxapi/regional_pricing/pricing.py -- change the two together or not at all.
 */

/** @returns {Promise<object>} region context; `enabled: false` shape on any failure. */
export const fetchRegionContext = async () => {
  const url = `${getConfig().LMS_BASE_URL}/blendxapi/pricing/region-context/`;
  const { data } = await getAuthenticatedHttpClient().get(url);
  return data;
};

let regionContextPromise = null;

/**
 * The region context, fetched at most ONCE per page load and shared.
 *
 * A discovery page renders many cards and every one needs the discount, so a
 * per-component fetch would fire N identical requests. The learner's region
 * cannot change mid-session, so memoising the promise is both correct and the
 * only sane load profile.
 *
 * Failures resolve to `null` (not a rejection) and stay cached: one failed
 * lookup means list prices for this page load, rather than every card
 * retrying a broken endpoint.
 */
export const getRegionContextOnce = () => {
  if (!regionContextPromise) {
    regionContextPromise = fetchRegionContext().catch(() => null);
  }
  return regionContextPromise;
};

/** Test seam: drop the memo so each test starts clean. */
export const __resetRegionContextCache = () => { regionContextPromise = null; };

/**
 * Apply `discountPct` to `baseAmount`, matching blendxapi exactly.
 *
 * Mirrors `regional_price` (pricing.py):
 *   - 0% is a TRUE no-op: the amount is returned untouched and unrounded, so
 *     enabling the feature never changes the price for an undiscounted learner;
 *   - otherwise round HALF-UP to a whole currency unit, then clamp at 0.
 *
 * JS rounds half-away-from-zero via Math.round for positives, which matches
 * ROUND_HALF_UP here because prices are never negative. The clamp keeps a
 * nonsense percentage from producing a negative price.
 */
export const applyRegionalPrice = (baseAmount, discountPct) => {
  // `Number(null)` is 0, not NaN, so a null price would otherwise be "discounted"
  // to 0 -- i.e. rendered as Free. Reject nullish before coercing.
  if (baseAmount === null || baseAmount === undefined) { return baseAmount; }
  const base = Number(baseAmount);
  if (!Number.isFinite(base)) { return baseAmount; }
  if (!discountPct) { return baseAmount; }

  const multiplier = (100 - Number(discountPct)) / 100;
  return Math.max(0, Math.round(base * multiplier));
};

/** The pre-discount figure to strike through, rounded the way the server does. */
export const originalPriceFor = (baseAmount, discountPct) => {
  if (baseAmount === null || baseAmount === undefined) { return baseAmount; }
  const base = Number(baseAmount);
  if (!Number.isFinite(base) || !discountPct) { return baseAmount; }
  return Math.round(base);
};
